import * as yup from "yup";
import {
  IoSaveOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
} from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import ReactSelect from "../../../../../components/common/ReactSelect";
import RadioButtonCard from "../../../../../components/common/RadioButtonCard";
import Toaster from "../../../../../components/common/Toaster";
import accountService from "../../../../../services/accountService";

const schema = yup.object({
  id: yup.string(),
  annual_income: yup.string().required("Please answer the question."),
  income_payor: yup.string().required("Please answer the question."),
  professional_partnership: yup
    .string()
    .required("Please answer the question."),
  sources: yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string().required("Please select at least 1 income."),
        value: yup.string().required("Please select at least 1 income."),
      })
    )
    .min(1, "Please select at least 1 income.")
    .required("Please select at least 1 income.")
    .nullable(),
});

const IncomeForm = ({ data, isLoading, setLoading, onSuccess }) => {
  const [incomeSources, setIncomeSources] = useState([]);

  const items = [
    {
      id: 1,
      value: true,
      content: (
        <div className="flex justify-between">
          <IoCheckmarkOutline className="text-green-500 text-lg" />
          <span className="text-gray-400 text-sm">Yes</span>
        </div>
      ),
    },
    {
      id: 2,
      value: false,
      content: (
        <div className="flex justify-between">
          <IoCloseOutline className="text-red-500 text-lg" />
          <span className="text-gray-400 text-sm">No</span>
        </div>
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchIncomeSources = () => {
      const data = accountService.getAccountIncomeSources();
      setIncomeSources(data);
    };

    fetchIncomeSources();
  }, []);

  useEffect(() => {
    const populateForm = () => {
      const selected_income = [];

      if (data.source_regular) {
        selected_income.push({
          label: "Regular Employee",
          value: 4,
        });
      }

      if (data.source_contractual) {
        selected_income.push({
          label: "Contractual Employee",
          value: 2,
        });
      }

      if (data.source_clinic) {
        selected_income.push({
          label: "Clinic",
          value: 1,
        });
      }

      setValue("id", data.id);
      setValue("sources", selected_income);
    };

    if (data) populateForm();
  }, [data, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await accountService.saveAccountIncomeInfoAsync(data);
      Toaster.Success("Information Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-2">
        <div className="col-span-2">
          <ReactSelect
            id="sources-dropdown"
            name="sources"
            title="Sources of Income"
            options={incomeSources}
            control={control}
            register={register("sources")}
            errors={errors}
            multiple
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <RadioButtonCard
            name="annual_income"
            title="Earning above 3M above annually"
            items={items}
            clearErrors={clearErrors}
            setValue={setValue}
            value={data.annual_income}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <RadioButtonCard
            name="income_payor"
            title="More than 1 income payor"
            items={items}
            clearErrors={clearErrors}
            setValue={setValue}
            value={data.income_payor}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <RadioButtonCard
            name="professional_partnership"
            title="Partner with GPP"
            items={items}
            clearErrors={clearErrors}
            setValue={setValue}
            value={data.professional_partnership}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default IncomeForm;
